import React from "react";
import { Helmet } from "react-helmet-async";
import { useLang } from "../contexts/LanguageContext";

const BASE_URL = "https://odisha-farmers-trust.preview.emergentagent.com";

/**
 * Per-page SEO component. Provides:
 *  - <title>, meta description, keywords
 *  - Open Graph + Twitter
 *  - canonical + hreflang alternates
 *  - Optional JSON-LD schema(s)
 *
 * Props:
 *   metaKey: string — dot path into translations.meta (e.g. "home", "about")
 *   pathname: string — path without /:lang prefix (e.g. "/about")
 *   image: optional OG image (defaults to logo)
 *   schemas: optional array of JSON-LD objects
 */
export default function SEO({ metaKey, pathname = "/", image, schemas = [] }) {
  const { t, lang, langs } = useLang();
  const title = t(`meta.${metaKey}.title`);
  const desc = t(`meta.${metaKey}.desc`);
  const kw = t(`meta.${metaKey}.kw`);
  const siteName = t("meta.site_name");
  const ogImage = image || `${BASE_URL}/images/logo.png`;

  const cleanPath = pathname === "/" ? "" : pathname.startsWith("/") ? pathname : `/${pathname}`;
  const canonical = `${BASE_URL}/${lang}${cleanPath}`;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={kw} />
      <meta name="robots" content="index, follow" />
      <meta name="author" content={siteName} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={lang === "en" ? "en_IN" : lang === "or" ? "or_IN" : "hi_IN"} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* Canonical + hreflang */}
      <link rel="canonical" href={canonical} />
      {langs.map((l) => (
        <link key={l.code} rel="alternate" hrefLang={l.code === "or" ? "or-IN" : l.code === "hi" ? "hi-IN" : "en-IN"} href={`${BASE_URL}/${l.code}${cleanPath}`} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${BASE_URL}/en${cleanPath}`} />

      {/* JSON-LD schemas */}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

/** Global Organization + LocalBusiness JSON-LD — included once on every page */
export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": ["NGO", "Organization", "LocalBusiness"],
    name: "Loka Samabaya Pratisthan",
    alternateName: "LSP Trust",
    url: BASE_URL,
    logo: `${BASE_URL}/images/logo.png`,
    image: `${BASE_URL}/images/gallery/lsp-1.jpg`,
    description:
      "Registered Trust in Odisha, India promoting natural farming, farmer training, indigenous seed preservation and sustainable rural development.",
    foundingLocation: "Niali, Cuttack, Odisha",
    areaServed: [
      { "@type": "AdministrativeArea", name: "Niali" },
      { "@type": "AdministrativeArea", name: "Cuttack" },
      { "@type": "AdministrativeArea", name: "Bhubaneswar" },
      { "@type": "AdministrativeArea", name: "Khordha" },
      { "@type": "AdministrativeArea", name: "Dhenkanal" },
      { "@type": "State", name: "Odisha" },
    ],
    address: {
      "@type": "PostalAddress",
      streetAddress: "Niali, Cuttack",
      addressLocality: "Bhubaneswar",
      postalCode: "751021",
      addressRegion: "Odisha",
      addressCountry: "IN",
    },
    contactPoint: [{
      "@type": "ContactPoint",
      telephone: "+91-99380-56120",
      contactType: "customer support",
      areaServed: "IN",
      availableLanguage: ["en", "or", "hi"],
    }],
    email: "lspnaturalfarming@gmail.com",
    telephone: "+91-99380-56120",
    sameAs: [],
  };
}

/** FAQPage schema for AEO */
export function faqSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
}

/** BreadcrumbList helper */
export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem", position: i + 1, name: it.name, item: it.url,
    })),
  };
}

/** Article schema for blog posts */
export function articleSchema({ title, description, image, datePublished, slug, lang }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    image: image ? (image.startsWith("http") ? image : `${BASE_URL}${image}`) : `${BASE_URL}/images/logo.png`,
    datePublished,
    author: { "@type": "Organization", name: "Loka Samabaya Pratisthan" },
    publisher: {
      "@type": "Organization",
      name: "Loka Samabaya Pratisthan",
      logo: { "@type": "ImageObject", url: `${BASE_URL}/images/logo.png` },
    },
    mainEntityOfPage: `${BASE_URL}/${lang}/blog/${slug}`,
  };
}

export { BASE_URL };
